import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { CareShell } from "@/components/CareShell";
import { loadRazorpay, openRazorpay } from "@/lib/razorpay";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { Wallet, Plus, ArrowDownLeft, ArrowUpRight, Loader2, X } from "lucide-react";

const QUICK = [500, 1000, 2500, 5000];

export default function CareWallet() {
    const [data, setData] = useState(null);
    const [sheet, setSheet] = useState(false);
    const [detail, setDetail] = useState(null);
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/wallet").then((r) => setData(r.data)).catch(() => setData({ balance: 0, transactions: [] }));
    useEffect(() => { load(); }, []);

    const recharge = async () => {
        const amt = Number(amount);
        if (!amt || amt < 100) return toast.error("Minimum recharge is ₹100");
        setBusy(true);
        try {
            const { data: order } = await api.post("/wallet/recharge/order", { amount: amt });
            const ok = await loadRazorpay();
            if (!ok) { toast.error("Could not load payment gateway."); setBusy(false); return; }
            openRazorpay(order, {
                description: `Wallet recharge · ${rupee(amt)}`,
                onSuccess: async (res) => {
                    try {
                        const { data: r } = await api.post("/wallet/recharge/verify", {
                            razorpay_order_id: res.razorpay_order_id, razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature,
                        });
                        toast.success(`Wallet recharged — balance ${rupee(r.balance)}`);
                        setSheet(false); setAmount(""); load();
                    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
                    finally { setBusy(false); }
                },
                onDismiss: () => { setBusy(false); toast("Recharge cancelled"); },
                onError: () => { setBusy(false); toast.error("Payment failed. Try again."); },
            });
        } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
    };

    return (
        <CareShell>
            <div className="px-5 pt-2">
                <h1 className="font-heading text-2xl font-extrabold text-navy">Care Wallet</h1>
                <p className="text-sm text-navy/55">Recharge once and pay for Care visits & plans instantly.</p>

                <div data-testid="wallet-balance-card" className="mt-4 rounded-3xl bg-navy p-6 text-white" style={{ boxShadow: "0 16px 32px -14px rgba(16,42,67,0.5)" }}>
                    <div className="flex items-center gap-2 text-white/70"><Wallet size={16} /> <span className="text-xs font-semibold uppercase tracking-wide">Available balance</span></div>
                    <p data-testid="wallet-balance" className="mt-1 font-heading text-4xl font-extrabold">{data ? rupee(data.balance) : "—"}</p>
                    <button data-testid="wallet-recharge-btn" onClick={() => setSheet(true)} className="mt-4 flex items-center gap-2 rounded-full bg-[#F43F5E] px-5 py-2.5 text-sm font-bold text-white active:scale-95"><Plus size={16} /> Recharge</button>
                </div>

                <h2 className="mt-6 font-heading text-base font-extrabold text-navy">History</h2>
                {data === null && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#F43F5E]" /></div>}
                {data && data.transactions.length === 0 && <div className="mt-3 rounded-2xl border border-dashed border-navy/15 py-10 text-center text-sm text-navy/45">No transactions yet. Recharge to get started.</div>}
                <div className="mt-3 space-y-2.5 pb-4">
                    {data?.transactions.map((t) => {
                        const credit = t.direction === "credit";
                        return (
                            <button key={t.id} data-testid={`wallet-txn-${t.id}`} onClick={() => setDetail(t)} className="flex w-full items-center gap-3 rounded-2xl bg-white p-3.5 text-left transition-transform active:scale-[0.99]" style={{ boxShadow: "0 8px 20px -12px rgba(16,42,67,0.1)" }}>
                                <div className={`flex h-10 w-10 items-center justify-center rounded-full ${credit ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>{credit ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}</div>
                                <div className="flex-1 overflow-hidden">
                                    <p className="truncate text-sm font-bold text-navy">{t.note || (credit ? "Recharge" : "Payment")}</p>
                                    <p className="text-xs text-navy/45">{new Date(t.created_at).toLocaleString()}</p>
                                </div>
                                <div className="text-right">
                                    <p className={`font-heading text-sm font-extrabold ${credit ? "text-emerald-600" : "text-navy"}`}>{credit ? "+" : "−"}{rupee(t.amount)}</p>
                                    <p className="text-[10px] text-navy/40">Bal {rupee(t.balance_after)}</p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {sheet && (
                <div className="fixed inset-0 z-[70] flex items-end justify-center bg-navy/50" onClick={() => setSheet(false)}>
                    <div className="w-full max-w-md rounded-t-3xl bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()}>
                        <div className="mb-3 flex items-center justify-between"><h3 className="font-heading text-lg font-extrabold text-navy">Recharge wallet</h3><button onClick={() => setSheet(false)} className="rounded-full bg-navy/5 p-1.5"><X size={16} /></button></div>
                        <div className="flex flex-wrap gap-2">
                            {QUICK.map((q) => (
                                <button key={q} data-testid={`wallet-quick-${q}`} onClick={() => setAmount(String(q))} className={`rounded-full border px-4 py-2 text-sm font-bold ${Number(amount) === q ? "border-[#F43F5E] bg-[#F43F5E]/10 text-[#F43F5E]" : "border-navy/15 text-navy/70"}`}>{rupee(q)}</button>
                            ))}
                        </div>
                        <input data-testid="wallet-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount (min ₹100)" className="mt-3 w-full rounded-xl border border-navy/15 px-3 py-3 text-sm outline-none focus:border-[#F43F5E]" />
                        <button data-testid="wallet-pay-btn" disabled={busy} onClick={recharge} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#F43F5E] py-3.5 text-sm font-bold text-white disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />} RECHARGE {amount ? rupee(Number(amount)) : ""}</button>
                    </div>
                </div>
            )}

            {detail && (
                <div className="fixed inset-0 z-[70] flex items-end justify-center bg-navy/50" onClick={() => setDetail(null)}>
                    <div className="w-full max-w-md rounded-t-3xl bg-white p-6 pb-8" onClick={(e) => e.stopPropagation()} data-testid="wallet-txn-detail">
                        <div className="mb-3 flex items-center justify-between"><h3 className="font-heading text-lg font-extrabold text-navy">Transaction details</h3><button data-testid="wallet-detail-close" onClick={() => setDetail(null)} className="rounded-full bg-navy/5 p-1.5"><X size={16} /></button></div>
                        <div className="flex items-center gap-3 rounded-2xl bg-navy/[0.03] p-4">
                            <div className={`flex h-12 w-12 items-center justify-center rounded-full ${detail.direction === "credit" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>{detail.direction === "credit" ? <ArrowDownLeft size={22} /> : <ArrowUpRight size={22} />}</div>
                            <div className="flex-1"><p className="text-sm font-bold text-navy">{detail.note || (detail.direction === "credit" ? "Recharge" : "Payment")}</p><p className="text-xs text-navy/45">{new Date(detail.created_at).toLocaleString()}</p></div>
                            <p className={`font-heading text-lg font-extrabold ${detail.direction === "credit" ? "text-emerald-600" : "text-navy"}`}>{detail.direction === "credit" ? "+" : "−"}{rupee(detail.amount)}</p>
                        </div>
                        <div className="mt-4 space-y-2.5 text-sm">
                            <Row label="Type" value={TXN_LABELS[detail.type] || detail.type} />
                            <Row label="Direction" value={detail.direction === "credit" ? "Credit (money in)" : "Debit (money out)"} />
                            <Row label="Amount" value={rupee(detail.amount)} />
                            <Row label="Balance after" value={rupee(detail.balance_after)} />
                            {detail.reference && <Row label="Reference" value={detail.reference} />}
                            <Row label="Date & time" value={new Date(detail.created_at).toLocaleString()} />
                        </div>
                    </div>
                </div>
            )}
        </CareShell>
    );
}

const TXN_LABELS = { care_payment: "Plan / visit payment", care_refund: "Plan cancellation refund", recharge: "Wallet recharge", care_recharge: "Wallet recharge" };

function Row({ label, value }) {
    return <div className="flex items-center justify-between border-b border-navy/5 pb-2"><span className="text-navy/50">{label}</span><span className="font-semibold text-navy">{value}</span></div>;
}
